import React, { useState, useCallback } from "react";
import { Loader2 } from "lucide-react";
import { api } from "../lib/api";

// Reusable Razorpay checkout abstraction. Supports:
//   mode="subscription" → recurring (Razorpay Subscriptions)
//   mode="order"        → one-time (Razorpay Orders, e.g. Lifetime)
//   mode="trial"        → server-side trial activation (no Checkout modal)
// Never marks the user as paid on its own — it always sends the Checkout
// result to the backend verify endpoint and reports the backend's answer.

// Single cached SDK-load promise shared across every button instance + any
// early preload call from the Billing page — avoids duplicate <script> tags
// and lets checkout open instantly once the SDK is already warm.
let _sdkPromise = null;
function loadCheckoutScript() {
  if (window.Razorpay) return Promise.resolve(true);
  if (_sdkPromise) return _sdkPromise;
  _sdkPromise = new Promise((resolve, reject) => {
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => { _sdkPromise = null; reject(new Error("Failed to load Razorpay Checkout")); };
    document.body.appendChild(s);
  });
  return _sdkPromise;
}

// Exported so the Billing page can warm the SDK on mount/hover, well before
// the user clicks a plan — pure perf, zero change to payment logic.
export const preloadRazorpaySDK = () => loadCheckoutScript().catch(() => {});

export const RazorpayCheckoutButton = ({
  mode = "order",
  planCode,
  couponCode,
  label,
  disabled,
  className,
  testid,
  onResult,
}) => {
  const [loading, setLoading] = useState(false);

  const report = useCallback((r) => { if (onResult) onResult(r); }, [onResult]);

  const run = useCallback(async () => {
    setLoading(true);
    report({ stage: "start", mode, planCode });
    try {
      // Trial: no Checkout modal — pure server activation.
      if (mode === "trial") {
        const res = await api.billingTrialStart();
        report(res.ok
          ? { stage: "verified", ok: true, entitlement: res.data?.entitlement, raw: res.data }
          : { stage: "error", ok: false, error: res.data?.detail || res.error });
        return;
      }

      // 1) Create order/subscription server-side (authoritative price/plan).
      const create = mode === "subscription"
        ? await api.billingSubscriptionCreate(planCode, couponCode)
        : await api.billingOrderCreate(planCode, couponCode);
      if (!create.ok) {
        report({ stage: "create_failed", ok: false, error: create.data?.detail || create.error, raw: create.data });
        return;
      }
      const d = create.data;
      report({ stage: "created", ok: true, raw: d });

      await loadCheckoutScript();

      const options = {
        key: d.razorpay_key_id,
        name: "C-ORE",
        description: `${(planCode || "").toUpperCase()} plan`,
        prefill: {
          name: d.customer_prefill?.name || "",
          email: d.customer_prefill?.email || "",
          contact: d.customer_prefill?.contact || "",
        },
        theme: { color: "#1E3A5F" },
        modal: {
          ondismiss: () => report({ stage: "dismissed", ok: false, error: "Checkout closed by user" }),
        },
      };

      if (mode === "subscription") {
        options.subscription_id = d.subscription_id;
        options.handler = async (resp) => {
          report({ stage: "checkout_success", ok: true, raw: resp });
          const v = await api.billingSubscriptionVerify({
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_subscription_id: resp.razorpay_subscription_id,
            razorpay_signature: resp.razorpay_signature,
          });
          report(v.ok
            ? { stage: "verified", ok: true, entitlement: v.data?.entitlement, raw: v.data }
            : { stage: "verify_failed", ok: false, error: v.data?.detail || v.error, raw: v.data });
        };
      } else {
        options.amount = d.amount_paise;
        options.currency = d.currency;
        options.order_id = d.order_id;
        options.handler = async (resp) => {
          report({ stage: "checkout_success", ok: true, raw: resp });
          const v = await api.billingPaymentVerify({
            razorpay_order_id: resp.razorpay_order_id,
            razorpay_payment_id: resp.razorpay_payment_id,
            razorpay_signature: resp.razorpay_signature,
          });
          report(v.ok
            ? { stage: "verified", ok: true, entitlement: v.data?.entitlement, raw: v.data }
            : { stage: "verify_failed", ok: false, error: v.data?.detail || v.error, raw: v.data });
        };
      }

      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", (resp) =>
        report({ stage: "payment_failed", ok: false, error: resp?.error?.description || "Payment failed", raw: resp?.error }));
      rzp.open();
    } catch (e) {
      report({ stage: "error", ok: false, error: e.message });
    } finally {
      setLoading(false);
    }
  }, [mode, planCode, couponCode, report]);

  return (
    <button
      type="button"
      onClick={run}
      disabled={disabled || loading}
      data-testid={testid}
      className={className || "inline-flex items-center justify-center gap-1.5 rounded-lg bg-[#1E3A5F] px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-[#16304d] disabled:opacity-50"}
    >
      {loading
        ? (<><Loader2 className="h-3.5 w-3.5 animate-spin shrink-0" /><span>{mode === "trial" ? "Starting trial…" : "Preparing secure checkout…"}</span></>)
        : (label || (mode === "trial" ? "Start free trial" : mode === "subscription" ? "Subscribe" : "Pay"))}
    </button>
  );
};

export default RazorpayCheckoutButton;
